import React, { useMemo } from "react";
import { useAuth } from "../../../../../context/AuthContext";
import "./AccountTab.css";

// Decode a base64url JWT payload without verifying — purely for the
// debug section below. Never trust the contents for any access
// decision; the backend already verifies the signature on every call.
function decodeJwtPayload(token) {
  try {
    const parts = String(token || "").split(".");
    if (parts.length < 2) return null;
    let b64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    while (b64.length % 4) b64 += "=";
    return JSON.parse(atob(b64));
  } catch {
    return null;
  }
}

const AccountTab = () => {
  const { userInfo, userRole, logout } = useAuth();

  // Pull the live ID token straight from sessionStorage and decode it
  // so we can show the operator exactly what the server is going to
  // see. Useful for "I was promoted to Captain but the UI says I'm
  // not" debugging — if cognito:groups doesn't include "Captains"
  // here, the JWT is stale and a fresh login is needed.
  const tokenInfo = useMemo(() => {
    if (typeof window === "undefined" || !window.sessionStorage) return null;
    const token = window.sessionStorage.getItem("cognitoIdToken");
    if (!token) return null;
    const payload = decodeJwtPayload(token);
    if (!payload) return { error: "Token present but could not be decoded." };
    const groupsRaw = payload["cognito:groups"];
    const groups = Array.isArray(groupsRaw)
      ? groupsRaw
      : typeof groupsRaw === "string" && groupsRaw.length
      ? groupsRaw.split(/[\s,]+/).filter(Boolean)
      : [];
    const exp = payload.exp ? new Date(payload.exp * 1000) : null;
    const iat = payload.iat ? new Date(payload.iat * 1000) : null;
    return {
      email: payload.email || null,
      sub: payload.sub || null,
      groups,
      tokenUse: payload.token_use || null,
      issuedAt: iat,
      expiresAt: exp,
      isExpired: exp ? exp.getTime() < Date.now() : null,
    };
  }, [userInfo]); // recompute when AuthContext updates

  return (
    <div className="account-section">
      <div className="user-profile">
        <h2>Account Settings</h2>
        {userInfo && (
          <div className="account-details">
            <p className="account-email">
              <strong>Email:</strong> {userInfo.email}
            </p>
            <p className="account-role">
              <strong>Role (frontend):</strong> {userRole}
            </p>
          </div>
        )}

        {/* Your access — diagnostic. Shows what the JWT actually carries
            so we can tell "I was promoted but the UI says no" apart from
            "the stale token in this browser doesn't know about the
            promotion yet." If Captains isn't in groups below but you
            were promoted in AWS, do a full sign-out + sign-in (and
            clear browser storage if that doesn't fix it). */}
        <details className="account-debug" open>
          <summary>
            <strong>Your access (what the server sees)</strong>
          </summary>
          {!tokenInfo && (
            <p className="account-debug__row">
              No ID token in session storage — you're not signed in.
            </p>
          )}
          {tokenInfo && tokenInfo.error && (
            <p className="account-debug__row">{tokenInfo.error}</p>
          )}
          {tokenInfo && !tokenInfo.error && (
            <>
              <p className="account-debug__row">
                <strong>Cognito sub:</strong>{" "}
                <code>{tokenInfo.sub || "—"}</code>
              </p>
              <p className="account-debug__row">
                <strong>cognito:groups:</strong>{" "}
                {tokenInfo.groups.length === 0 ? (
                  <em>(none — only baseline signed-in access)</em>
                ) : (
                  tokenInfo.groups.map((g) => (
                    <code
                      key={g}
                      className={
                        g === "Admins"
                          ? "account-debug__group account-debug__group--admin"
                          : g === "Captains"
                          ? "account-debug__group account-debug__group--captain"
                          : "account-debug__group"
                      }
                    >
                      {g}
                    </code>
                  ))
                )}
              </p>
              <p className="account-debug__row">
                <strong>Token use:</strong> {tokenInfo.tokenUse || "—"}
              </p>
              <p className="account-debug__row">
                <strong>Issued:</strong>{" "}
                {tokenInfo.issuedAt
                  ? tokenInfo.issuedAt.toLocaleString()
                  : "—"}
              </p>
              <p className="account-debug__row">
                <strong>Expires:</strong>{" "}
                {tokenInfo.expiresAt
                  ? tokenInfo.expiresAt.toLocaleString()
                  : "—"}
                {tokenInfo.isExpired && (
                  <em> — expired; sign in again</em>
                )}
              </p>
            </>
          )}
          <p className="account-debug__hint">
            If you were just promoted to Captain but don't see <code>Captains</code> above,
            this browser is using a cached token from before the promotion. Sign out, then
            sign in again (or clear browser storage / use a private tab) to pick up the
            new group.
          </p>
        </details>

        <button onClick={logout} className="logout-btn">
          Logout
        </button>
      </div>
    </div>
  );
};

export default AccountTab;
